import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Target, Users, Award, Rocket } from 'lucide-react';

const AboutPage = () => {
  const values = [
    {
      icon: Target,
      title: "Mission-Driven",
      description: "We're on a mission to democratize enterprise-grade pricing intelligence for SMBs worldwide."
    },
    {
      icon: Users,
      title: "Customer-First",
      description: "Every feature we build starts with real SMB challenges and ends with measurable business impact."
    },
    {
      icon: Award,
      title: "Excellence",
      description: "We hold ourselves to the highest standards of quality, reliability, and customer support."
    },
    {
      icon: Rocket,
      title: "Innovation",
      description: "Constantly pushing the boundaries of what's possible with AI and adaptive intelligence."
    }
  ];

  const milestones = [
    { 
      year: "Q3 2025", 
      status: "complete",
      title: "PoC Complete",
      event: "Core pricing engine validated 2.5 Billion synthetic prices. First pricing foundation model created. 35 use cases designed and architected. Technical foundation proven."
    },
    { 
      year: "Q4 2025", 
      status: "current",
      title: "Alpha Launch - Project Everest",
      event: "10 pilot customers across 3 verticals. Real customer data integration and validation."
    },
    { 
      year: "Q1 2026", 
      status: "planned",
      title: "Beta Launch + First Revenue",
      event: "Public beta release with expanded feature set and first commercial customers."
    }
  ];

  return (
    <div>
      <Header />
      
      {/* Hero */}
      <section className="pt-40 pb-20 px-6 bg-gradient-to-b from-[#0A0F1C] to-[#111827]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="text-6xl md:text-7xl font-bold text-[#F8FAFC] mb-6">
              An
              <a href="https://axiosera.com" target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
                <span className="bg-gradient-to-r from-[#2563EB] to-[#3B82F6] bg-clip-text text-transparent"> AxioSera </span>
              </a>
              Product
            </h1>
            <p className="text-xl text-[#94A3B8] max-w-3xl mx-auto">
              The adaptive intelligence platform bringing enterprise-grade decision infrastructure to every business.
            </p>
          </div>

          {/* AxioSera Logo */}
          <a href="https://axiosera.com" target="_blank" rel="noopener noreferrer" className="block">
            <div className="relative rounded-2xl overflow-hidden border border-[#2563EB]/30 shadow-2xl bg-gradient-to-br from-[#0A0F1C] to-[#111827] p-12 hover:border-[#2563EB]/50 transition-colors">
              <img
                src="/axiosera-logo.png"
                alt="AxioSera"
                className="w-full max-w-md mx-auto"
              />
            </div>
          </a>
        </div>
      </section>

      {/* Story */}
      <section className="py-24 px-6 bg-[#0A0F1C]">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-5xl font-bold text-[#F8FAFC] mb-8">About AxioSera</h2>
          
          {/* Leadership Video */}
          <div className="relative rounded-2xl overflow-hidden border border-[#2563EB]/30 shadow-2xl shadow-[#2563EB]/20 mb-12">
            <video 
              controls
              className="w-full aspect-video bg-[#0A0F1C]"
              poster="/leadership-poster.jpg"
            >
              <source 
                src="https://customer-assets.emergentagent.com/job_price-optimizer-12/artifacts/st330uwr_Man%20in%20Suit.mp4" 
                type="video/mp4" 
              />
              Your browser does not support the video tag.
            </video>
          </div>
          
          <div className="space-y-6 text-lg text-[#94A3B8] leading-relaxed">
            <p className="text-[#F8FAFC] font-semibold text-2xl">
              AxioSera is the platform. PriceStrike is one of its products.
            </p>
            <p>
              <span className="text-[#2563EB] font-semibold">Mission:</span> Bring adaptive intelligence to every business through composable infrastructure.
            </p>
            <p>
              <span className="text-[#2563EB] font-semibold">Architecture:</span> Our modular, governed, API-first system powers all products in the AxioSera ecosystem. We've built a foundation that enables businesses to integrate adaptive intelligence seamlessly into their operations.
            </p>
            <p>
              <span className="text-[#2563EB] font-semibold">Vision:</span> Become the leading adaptive decision infrastructure provider, democratizing enterprise-grade AI capabilities for businesses of all sizes.
            </p>
            <p className="text-[#F8FAFC] font-semibold">
              Backed by proven leadership, enterprise-grade design, and live products.
            </p>
            <p>
              PriceStrike represents our first step in this mission — bringing adaptive pricing intelligence to SMBs worldwide. Through our composable infrastructure approach, we're making it possible for any business to leverage sophisticated AI systems that drive real results.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 px-6 bg-gradient-to-b from-[#0A0F1C] to-[#111827]">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-5xl font-bold text-[#F8FAFC] text-center mb-16">
            Our <span className="text-[#2563EB]">Values</span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div key={index} className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/30 rounded-xl p-8 hover:border-[#2563EB] transition-all duration-300">
                  <div className="w-16 h-16 bg-[#2563EB]/20 rounded-lg flex items-center justify-center mb-6">
                    <Icon className="w-8 h-8 text-[#2563EB]" />
                  </div>
                  <h3 className="text-2xl font-bold text-[#F8FAFC] mb-4">{value.title}</h3>
                  <p className="text-[#94A3B8] leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Traction & Product Roadmap */}
      <section className="py-24 px-6 bg-[#0A0F1C]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-5xl font-bold text-[#F8FAFC] text-center mb-4">
            Traction & Product <span className="text-[#2563EB]">Roadmap</span>
          </h2>
          <p className="text-[#94A3B8] text-center mb-16 text-lg">
            Our journey from proof of concept to market-ready product
          </p>
          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-green-500 via-[#2563EB] to-[#2563EB]/30" />
            
            <div className="space-y-8">
              {milestones.map((milestone, index) => (
                <div key={index} className="flex gap-6 items-start relative">
                  {/* Status Indicator */}
                  <div className={`flex-shrink-0 w-16 h-16 rounded-full flex items-center justify-center border-4 z-10 ${
                    milestone.status === 'complete' 
                      ? 'bg-green-500 border-green-400 text-white' 
                      : milestone.status === 'current'
                      ? 'bg-[#2563EB] border-[#3B82F6] text-white animate-pulse'
                      : 'bg-[#111827] border-[#2563EB]/30 text-[#94A3B8]'
                  }`}>
                    {milestone.status === 'complete' ? (
                      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : milestone.status === 'current' ? (
                      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    ) : (
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <circle cx="12" cy="12" r="3" strokeWidth={2} />
                      </svg>
                    )}
                  </div>

                  {/* Content Card */}
                  <div className={`flex-1 bg-gradient-to-br from-[#111827] to-[#0A0F1C] border rounded-2xl p-6 transition-all ${
                    milestone.status === 'complete' 
                      ? 'border-green-500/50 shadow-lg shadow-green-500/10' 
                      : milestone.status === 'current'
                      ? 'border-[#2563EB] shadow-lg shadow-[#2563EB]/20'
                      : 'border-[#2563EB]/20'
                  }`}>
                    <div className="flex items-center gap-3 mb-3">
                      <span className={`text-sm font-bold px-3 py-1 rounded-full ${
                        milestone.status === 'complete'
                          ? 'bg-green-500/20 text-green-400'
                          : milestone.status === 'current'
                          ? 'bg-[#2563EB]/20 text-[#2563EB]'
                          : 'bg-[#2563EB]/10 text-[#94A3B8]'
                      }`}>
                        {milestone.year}
                      </span>
                      {milestone.status === 'complete' && (
                        <span className="text-green-400 text-sm font-medium">✓ Complete</span>
                      )}
                      {milestone.status === 'current' && (
                        <span className="text-[#2563EB] text-sm font-medium">● In Progress</span>
                      )}
                      {milestone.status === 'planned' && (
                        <span className="text-[#94A3B8] text-sm font-medium">○ Planned</span>
                      )}
                    </div>
                    <h3 className="text-2xl font-bold text-[#F8FAFC] mb-2">{milestone.title}</h3>
                    <p className="text-[#94A3B8] leading-relaxed">{milestone.event}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 bg-gradient-to-b from-[#0A0F1C] to-[#111827]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-5xl font-bold text-[#F8FAFC] mb-6">
            Learn More About AxioSera
          </h2>
          <p className="text-xl text-[#94A3B8] mb-8">
            Discover how our adaptive intelligence platform is transforming business operations.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://axiosera.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-10 py-7 text-lg font-semibold rounded-lg">
                Visit AxioSera.com
              </Button>
            </a>
            <Link to="/contact">
              <Button variant="outline" className="border-[#2563EB] text-[#2563EB] hover:bg-[#2563EB]/10 px-10 py-7 text-lg font-semibold rounded-lg">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;